import { css } from '@emotion/react';
import { toRem } from '../functions';
import { heading1CSS, heading2CSS, heading3CSS, heading4CSS, heading5CSS, heading6CSS } from './typography';
export const globalStyles = css({
    /* RESET */
    ':root': {
        // ACCENT
        '--defaultAccent': '#3AC494',
        '--accentAccent': '#FFFFFF',
        '--accent': 'var(--defaultAccent)',
        // HEADER
        '--headerBackground': '#FFFFFF',
        '--headerText': '#1B3145',
        // BACKGROUND
        '--defaultBackgroundWebsite': '#F9FAFB',
        '--defaultBackgroundContent': '#FFFFFF',
        '--defaultBackgroundDropdown': '#FFFFFF',
        '--accentBackground': '#3AC494',
        '--accentBackgroundContent': '#3AC494',
        '--accentBackgroundDropdown': '#3A2E93',
        '--backgroundWebsite': 'var(--defaultBackgroundWebsite)',
        '--backgroundContent': 'var(--defaultBackgroundContent)',
        '--backgroundDropdown': 'var(--defaultBackgroundDropdown)',
        // BORDER
        '--border': '#E7EBEF',
        // TEXT
        '--defaultTextStrong': '#1B3145',
        '--defaultTextDefault': '#3E4C59',
        '--defaultTextSubtle': '#74808A',
        '--accentTextStrong': '#FFFFFF',
        '--accentTextDefault': '#F1F0FC',
        '--accentTextSubtle': '#C9C3F3',
        '--textStrong': 'var(--defaultTextStrong)',
        '--textDefault': 'var(--defaultTextDefault)',
        '--textSubtle': 'var(--defaultTextSubtle)',
        // TONE
        '--negativeSubtle': '#FDEEEF',
        '--negativeDefault': '#E12D39',
        '--negativeStrong': '#AB1721',
        '--positiveSubtle': '#F3FDEE',
        '--positiveDefault': '#67E12D',
        '--positiveStrong': '#419E16',
        '--warningSubtle': '#FDF9EE',
        '--warningDefault': '#E1AF2D',
        '--warningStrong': '#A17911',
    },
    'html': {
        fontFamily: `'Eina02', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif`,
        textRendering: 'optimizeLegibility',
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased'
    },
    'body': {
        backgroundColor: 'var(--backgroundWebsite)',
        margin: 0,
    },
    'h1, h2, h3, h4, h5, h6': {
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
        marginBlockStart: 0,
        marginBlockEnd: 0,
    },
    'p': {
        marginBlockStart: 0,
        marginBlockEnd: 0,
    },
    'ol, ul': {
        listStyle: 'none',
        marginBlockStart: 0,
        marginBlockEnd: 0,
        paddingInlineStart: 0,
    },
    'hr': {
        border: 'none',
        margin: 0,
    },
    'small': {
        fontSize: 'inherit',
    },
    'button': {
        appearance: 'none',
        background: 'none',
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        color: 'inherit',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
        margin: 0,
        padding: 0,
        textAlign: 'inherit',
    },
    'svg': {
        blockSize: 'auto',
        clipRule: 'evenodd',
        display: 'block',
        fill: 'inherit',
        fillRule: 'evenodd',
    },
    'a': {
        color: 'inherit',
        display: 'inline-block',
        textDecoration: 'none',
    },
    'input, select, textarea': {
        appearance: 'none',
        background: 'none',
        border: 'none',
        borderRadius: 0,
        boxShadow: 'none',
        color: 'inherit',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        letterSpacing: 'inherit',
        lineHeight: 'inherit',
        margin: 0,
        minWidth: 0,
        padding: 0,
    },
    '.article': {
        color: 'var(--textDefault)',
        fontSize: toRem(14),
        lineHeight: toRem(24),
        padding: '0 !important',
        '@media (min-width: 1200px)': {
            fontSize: toRem(16),
            lineHeight: toRem(28),
        },
        'p': {
            marginBlockStart: '1.5em',
            marginBlockEnd: '1.5em',
            '&:first-child': {
                marginBlockStart: 0,
            },
            '&:last-child': {
                marginBlockEnd: 0,
            },
        },
        'ul, ol': {
            paddingInlineStart: '3em',
            marginBlockStart: '1.5em',
            marginBlockEnd: '1.5em',
            'ul, ol': {
                marginBlockStart: '.75em',
                marginBlockEnd: '.75em',
            },
            '&:first-child': {
                marginBlockStart: 0,
            },
            '&:last-child': {
                marginBlockEnd: 0,
            },
        },
        'ul': {
            listStyle: 'disc',
        },
        'ol': {
            listStyle: 'decimal',
            'ol': {
                listStyle: 'lower-roman',
            },
        },
        'li': {
            marginBlockStart: '.75em',
            marginBlockEnd: '.75em',
        },
        'a, [data-elevio-article]': {
            color: 'var(--accent)',
            cursor: 'pointer',
            fontWeight: 600,
            outline: 'none',
            textDecoration: 'underline',
            transitionProperty: 'opacity',
            transitionDuration: '200ms',
            transitionTimingFunction: 'cubic-bezier(0,0,0,.4)',
            '&:hover': {
                opacity: .75,
            },
            '&:active': {
                opacity: .75,
            },
            '&:focus': {
                borderRadius: toRem(3),
                boxShadow: `0 0 0 ${toRem(2)} var(--accent)`,
                marginInlineStart: toRem(-2),
                marginInlineEnd: toRem(-2),
                paddingInlineStart: toRem(2),
                paddingInlineEnd: toRem(2),
            }
        },
        'h1, h2, h3, h4, h5, h6': {
            color: 'var(--textStrong)',
            marginBlockStart: '1.5em',
            marginBlockEnd: '1.5em',
            '&:first-child': {
                marginBlockStart: 0,
            },
            '&:last-child': {
                marginBlockEnd: 0,
            },
        },
        'h1': heading1CSS,
        'h2': heading2CSS,
        'h3': heading3CSS,
        'h4': heading4CSS,
        'h5': heading5CSS,
        'h6': heading6CSS,
        'blockquote': {
            fontStyle: 'italic',
            margin: `3em`,
            '&:first-child': {
                marginBlockStart: 0,
            },
            '&:last-child': {
                marginBlockEnd: 0,
            },
        },
        '.elevio-box-error, .elevio-box-info, .elevio-box-success, .elevio-box-warning': {
            borderRadius: toRem(6),
            fontStyle: 'normal',
            margin: '1.5em 0',
            padding: `${toRem(16)} ${toRem(20)}`,
            '&:first-child': {
                marginBlockStart: 0,
            },
            '&:last-child': {
                marginBlockEnd: 0,
            },
        },
        '.elevio-box-info': {
            '--accent': '#2BB0ED',
            backgroundColor: '#EEF9FE',
            color: '#016B9C',
        },
        '.elevio-box-success': {
            '--accent': 'var(--positiveDefault)',
            backgroundColor: 'var(--positiveSubtle)',
            color: 'var(--positiveStrong)',
        },
        '.elevio-box-error': {
            '--accent': 'var(--negativeDefault)',
            backgroundColor: 'var(--negativeSubtle)',
            color: 'var(--negativeStrong)',
        },
        '.elevio-box-warning': {
            '--accent': 'var(--warningDefault)',
            backgroundColor: 'var(--warningSubtle)',
            color: 'var(--warningStrong)',
        },
        'img': {
            blockSize: 'auto',
            marginBlockStart: '1.5em',
            marginBlockEnd: '1.5em',
            maxInlineSize: '100%',
            '&:first-child': {
                marginBlockStart: 0,
            },
            '&:last-child': {
                marginBlockEnd: 0,
            },
        },
        '.fr-video': {
            display: 'block',
            inlineSize: '100%',
            marginBlockStart: '1.5em',
            marginBlockEnd: '1.5em',
            paddingBlockEnd: '56.25%',
            position: 'relative',
            '&:first-child': {
                marginBlockStart: 0,
            },
            '&:last-child': {
                marginBlockEnd: 0,
            },
            'iframe': {
                blockSize: '100%',
                inlineSize: '100%',
                left: 0,
                position: 'absolute',
                top: 0,
            },
        },
        'code': {
            backgroundColor: 'var(--border)',
            borderRadius: toRem(3),
            color: 'var(--textDefault)',
            display: 'inline-block',
            fontSize: '.875em',
            paddingInlineStart: toRem(4),
            paddingInlineEnd: toRem(4),
        },
        'pre': {
            backgroundColor: 'var(--textStrong)',
            borderRadius: toRem(6),
            color: 'var(--accentTextStrong)',
            fontSize: '.875em',
            overflow: 'auto',
            padding: `.75em 1.5em`,
            WebkitOverflowScrolling: 'touch',
            'code': {
                backgroundColor: 'transparent',
                borderRadius: 0,
                color: 'inherit',
                display: 'block',
                fontSize: 'inherit',
                paddingInlineStart: 0,
                paddingInlineEnd: 0,
            },
        },
    },
    '.error-page': {
        '--accent': '(var--accentAccent)',
        '--backgroundWebsite': 'var(--accentBackground)',
        '--textStrong': 'var(--accentTextStrong)',
        '--textDefault': 'var(--accentTextDefault)',
        '--textSubtle': 'var(--accentTextSubtle)',
    },
});
